import React, { useCallback, useMemo, useState, useEffect } from 'react';
import { View, Text, TouchableOpacity, StyleSheet, ScrollView } from 'react-native';
import { SvgProps } from 'react-native-svg';
import { RESIDENTS_SVG_COMPONENTS } from '@/assets/residents';
import { 
  RESIDENT_NAV_COLORS,
  IResidentNavigationItem,
  IResidentUserState,
  NavigationGroup,
  NAVIGATION_GROUP_LABELS,
  NAVIGATION_GROUP_ORDER,
  getGroupedNavigationItems,
  RESIDENT_NAVIGATION_ITEMS,
} from '@/constants/residents/residentNavigation';
import { AnimatedAccordion } from '@/components/common/AnimatedAccordion';

type SvgComponent = React.FC<SvgProps & { color?: string }>;

const getGroupForPath = (path: string): NavigationGroup => {
  const item = RESIDENT_NAVIGATION_ITEMS.find(
    (navItem) => path === navItem.path || path.startsWith(navItem.path)
  );
  return item?.group || 'QUICK_ACTIONS';
};

interface ResidentNavListProps {
  currentPath: string;
  onNavigate: (path: string) => void;
  userState?: IResidentUserState;
}

export const ResidentNavList: React.FC<ResidentNavListProps> = ({ 
  currentPath, 
  onNavigate,
  userState = {},
}) => {
  const [expandedGroups, setExpandedGroups] = useState<Set<NavigationGroup>>(() => new Set([getGroupForPath(currentPath)]));
  const groupedItems = useMemo(() => getGroupedNavigationItems(userState), [userState]);

  useEffect(() => {
    const activeGroup = getGroupForPath(currentPath);
    setExpandedGroups((prev) => {
      if (prev.has(activeGroup)) return prev;
      const next = new Set(prev);
      next.add(activeGroup);
      return next;
    });
  }, [currentPath]);

  const toggleGroup = useCallback((group: NavigationGroup) => {
    setExpandedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(group)) {
        next.delete(group);
      } else {
        next.add(group);
      }
      return next;
    });
  }, []);

  const handleNavigate = useCallback((path: string) => {
    onNavigate(path);
  }, [onNavigate]);

  const renderNavItems = useCallback((items: IResidentNavigationItem[]) => {
    return items.map((item) => {
      const isActive = currentPath === item.path || currentPath.startsWith(item.path);
      const IconComponent = RESIDENTS_SVG_COMPONENTS[item.iconKey] as SvgComponent | undefined;

      return (
        <TouchableOpacity
          key={item.id}
          style={[styles.navItem, isActive && styles.navItemActive]}
          onPress={() => handleNavigate(item.path)}
          activeOpacity={0.7}
          accessibilityLabel={item.title}
          accessibilityHint={`Navigate to ${item.title}`}
          accessibilityRole="button"
          accessibilityState={{ selected: isActive }}
        >
          <View style={styles.iconContainer}>
            {IconComponent && (
              <IconComponent
                width={24}
                height={24}
                color={isActive ? RESIDENT_NAV_COLORS.TEXT_PRIMARY : RESIDENT_NAV_COLORS.TEXT_TERTIARY}
              />
            )}
          </View>
          <Text style={[styles.navItemText, isActive && styles.navItemTextActive]}>
            {item.title}
          </Text>
        </TouchableOpacity>
      );
    });
  }, [currentPath, handleNavigate]);

  return (
    <View style={styles.container}>
      <ScrollView 
        style={styles.scrollView} 
        contentContainerStyle={styles.scrollContent}
        showsVerticalScrollIndicator={false}
      >
        <View style={styles.navList}>
          {NAVIGATION_GROUP_ORDER.map((group) => {
            const items = groupedItems[group];
            if (!items || items.length === 0) return null;
            const isExpanded = expandedGroups.has(group);

            return (
              <AnimatedAccordion
                key={group}
                isExpanded={isExpanded}
                onToggle={() => toggleGroup(group)}
                containerStyle={styles.section}
                headerStyle={styles.sectionHeaderButton}
                contentStyle={styles.sectionItems}
                header={
                  <Text style={[styles.sectionHeader, isExpanded && styles.sectionHeaderActive]}>
                    {NAVIGATION_GROUP_LABELS[group]}
                  </Text>
                }
                chevron={
                  <RESIDENTS_SVG_COMPONENTS.CHEVRON_DOWN
                    width={16}
                    height={16}
                    color={isExpanded ? RESIDENT_NAV_COLORS.TEXT_PRIMARY : RESIDENT_NAV_COLORS.TEXT_TERTIARY}
                  />
                }
              >
                {renderNavItems(items)}
              </AnimatedAccordion>
            );
          })}
        </View>
      </ScrollView>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    width: '100%',
    flex: 1,
  },
  scrollView: {
    flex: 1,
  },
  scrollContent: {
    flexGrow: 1,
  },
  navList: {
    width: '100%',
    gap: 8,
  },
  section: {
    width: '100%',
  },
  sectionHeaderButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingVertical: 12,
    paddingHorizontal: 4,
  },
  sectionHeader: {
    fontFamily: 'System',
    fontWeight: '600',
    fontSize: 12,
    lineHeight: 18,
    letterSpacing: 0.5,
    textTransform: 'uppercase',
    color: RESIDENT_NAV_COLORS.TEXT_TERTIARY,
  },
  sectionHeaderActive: {
    color: RESIDENT_NAV_COLORS.TEXT_PRIMARY,
  },
  sectionItems: {
    width: '100%',
    gap: 10,
  },
  navItem: {
    width: '100%',
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    borderRadius: 16,
    borderWidth: 1,
    borderColor: RESIDENT_NAV_COLORS.BORDER_PRIMARY,
    paddingHorizontal: 16,
    paddingVertical: 10,
  },
  navItemActive: {
    borderWidth: 2,
    borderColor: RESIDENT_NAV_COLORS.TEXT_SECONDARY,
    backgroundColor: '#171717',
  },
  iconContainer: {
    width: 24,
    height: 24,
    alignItems: 'center',
    justifyContent: 'center',
  },
  navItemText: {
    flex: 1,
    fontFamily: 'System',
    fontWeight: '500',
    fontSize: 14,
    lineHeight: 21,
    color: RESIDENT_NAV_COLORS.TEXT_PRIMARY,
  },
  navItemTextActive: {
    fontWeight: '600',
  },
});
